import React, {useState, useEffect, useRef, useContext, useMemo} from 'react'
import {ReservationContext} from '../../../../context/Reservation'
import moment from 'moment'
import _ from 'lodash'
import LeftHandle from './LeftHandle'
import RightHandle from './RightHandle'

import './DateRange.scss'
import useReservationGanttContext from '../../../../context/ReservationGantt/ReservationGantt'
import {SettingsContext} from '../../../../context/Settings/Settings'
import useCalendarViewContext from '../../../../context/CalendarView'

const RootClass = 'ap-reservation-gantt'
const DateRange = ({columnHeaders, parentEl, currentDate}) => {
  const settings = useContext(SettingsContext)
  const {selectedViewGroup} = useCalendarViewContext()
  const refLeftHandle = useRef(null)
  const refRightHandle = useRef(null)
  const {update, reservation, configuration, setOpenReserveForPopOver} = useContext(
    ReservationContext
  )
  const {viewBreakPoint} = useReservationGanttContext()

  const canReserve = useMemo(() => {
    if (!configuration.data.active || !selectedViewGroup || !settings?.data) return false

    return (
      !!settings.data?.user_access_rules?.can_add?.reservations.find(
        f => f === selectedViewGroup.key
      ) || false
    )
  }, [selectedViewGroup, settings, configuration])

  useEffect(() => {
    const headers = columnHeaders.primary
    const snapMins = 30 * 60 * 1000 // 30 mins in ms
    if (!headers.length) return
    if (!canReserve) return
    const currentDateX = GetX(headers, moment(currentDate).valueOf())
    const width = headers[0].width
    //
    const ref = parentEl.current
    const leftHandle = refLeftHandle.current
    const rightHandle = refRightHandle.current
    // labels
    const leftLabel = leftHandle.querySelector('*[name="label"]')
    const rightLabel = rightHandle.querySelector('*[name="label"]')

    let isDrag = false

    let startX = GetRawX(headers, headers[0].sec + snapMins)
    let lastX = 0

    let initClientX = 0
    let initX = 0
    let offsetX = 0

    let prevRelX = 0

    let mode = null // range | left | right

    let snapDiff = GetRawX(headers, headers[0].sec + snapMins)

    let maxX = (headers.length - 1) * width

    // fix handle widths and positions
    if (reservation.isSelection && reservation.date_from && reservation.date_to) {
      startX = GetX(headers, moment(reservation.date_from).valueOf())
      lastX = GetX(headers, moment(reservation.date_to).valueOf())
      leftHandle.style.display = startX > 0 ? 'block' : 'none'
      leftHandle.style.width = startX + 'px'

      // update x
      rightHandle.style.display = lastX > 0 ? 'block' : 'none'
      rightHandle.style.left = lastX + 'px'
    } else {
      leftHandle.style.display = 'none'
      rightHandle.style.display = 'none'
    }

    const handleMouseMove = e => {
      if (mode === null) return
      let relX = _.clamp(e.clientX - initClientX + initX + offsetX, 26, maxX)
      relX = 26 + snap(relX - 26, snapDiff, Math.round)

      // check if don't allow relX to be less than to current date
      if (relX <= currentDateX) {
        // snapping will use ceil so it can't be less than the current date
        relX = 26 + snap(currentDateX - 26, snapDiff, Math.ceil)
      }

      if (!isDrag && Math.abs(relX - initX) < Math.max(20, 20)) return
      isDrag = true

      if (Math.abs(relX - initX) < Math.min(snapDiff, 20)) return

      isDrag = true
      if (relX < initX) {
        startX = 0 + relX
        lastX = 0 + initX
      } else {
        lastX = 0 + relX
        startX = 0 + initX
      }

      leftHandle.style.display = 'block'
      leftHandle.style.width = startX + 'px'

      rightHandle.style.display = 'block'
      rightHandle.style.left = lastX + 'px'

      if (prevRelX !== relX) {
        leftLabel.innerHTML = moment(snapSec(startX, width, snapMins, columnHeaders)).format(
          viewBreakPoint.rangeSelectorFormat
        )
        rightLabel.innerHTML = moment(snapSec(lastX, width, snapMins, columnHeaders)).format(
          viewBreakPoint.rangeSelectorFormat
        )
      }
      prevRelX = relX
    }

    const handleMouseDown = e => {
      if (e.target.getAttribute('data-x') !== 'date-selection') return
      let rect = ref.getBoundingClientRect()
      let _x = e.clientX - rect.left - 160

      if (_x < 0) return

      let relX = _.clamp(_x, 26, maxX)
      relX = 26 + snap(relX - 26, snapDiff, Math.round)

      // prevent date selection if starting point is before current date
      if (relX <= currentDateX) return

      if (e.target.getAttribute('name') === 'leftHandle') {
        mode = 'left'
        initX = parseFloat(leftHandle.style.width) || 0
        initX = lastX
        offsetX = startX - lastX
      } else if (e.target.getAttribute('name') === 'rightHandle') {
        mode = 'right'
        initX = startX
        offsetX = lastX - startX
      } else {
        mode = 'range'
        initX = relX
      }
      initClientX = e.clientX
    }

    const handleMouseUp = e => {
      if (isDrag) {
        update('date_from', moment(snapSec(startX, width, snapMins, columnHeaders)))
        update('date_to', moment(snapSec(lastX, width, snapMins, columnHeaders)))
        isDrag = false
        update('isSelection', true)
        if (!reservation.isSelection) {
          setOpenReserveForPopOver({
            anchorPosition: {top: e.clientY + 15, left: e.clientX - 52},
          })
        }
      }
      if (mode !== null && !isDrag && reservation.isSelection) {
        update('reserved_for', {})
        update('selected_items', [])
        update('isSelection', false)
      }
      offsetX = 0
      mode = null
    }

    // register events
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      mode = null
      isDrag = false

      // unregister events
      window.removeEventListener('mousemove', handleMouseDown)
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    parentEl,
    refLeftHandle,
    refRightHandle,
    columnHeaders,
    update,
    viewBreakPoint,
    reservation,
    canReserve,
    setOpenReserveForPopOver,
    currentDate,
  ])

  return (
    <div
      className="m-gantt c-date-range"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        left: 160,
        bottom: 0,
        zIndex: 200,
        pointerEvents: 'none',
      }}
    >
      <LeftHandle
        ref={refLeftHandle}
        label={
          !!reservation.date_from &&
          moment(reservation.date_from).format(viewBreakPoint.rangeSelectorFormat)
        }
      />
      <RightHandle
        ref={refRightHandle}
        label={
          !!reservation.date_to &&
          moment(reservation.date_to).format(viewBreakPoint.rangeSelectorFormat)
        }
      />
    </div>
  )
}

export default DateRange

// aux
const GetRawX = (headers, date_sec) => {
  if (!headers.length) return 0

  const nearestIndex = headers.findIndex(e => date_sec >= e.sec && date_sec <= e.endSec) // get first instance
  let right = 0

  // get percentage value
  if (nearestIndex !== -1) {
    const normNearest = headers[nearestIndex].endSec - headers[nearestIndex].sec
    const normStart = date_sec - headers[nearestIndex].sec
    const percStart = normStart / normNearest
    right = nearestIndex * headers[nearestIndex].width + percStart * headers[nearestIndex].width
  }

  const last = headers[headers.length - 1]
  return last.endSec < date_sec ? headers.length * last.width : right
}

const GetX = (headers, date_sec) => {
  if (!headers.length) return 0

  const nearestIndex = headers.findIndex(e => date_sec >= e.sec && date_sec <= e.endSec) // get first instance
  let right = 0

  // get percentage value
  if (nearestIndex !== -1) {
    const normNearest = headers[nearestIndex].endSec - headers[nearestIndex].sec
    const normStart = date_sec - headers[nearestIndex].sec
    const percStart = normStart / normNearest
    right = nearestIndex * headers[nearestIndex].width + percStart * headers[nearestIndex].width
  }

  const last = headers[headers.length - 1]
  const scrollAdjustment = headers[0].width - 26
  return (last.endSec < date_sec ? headers.length * last.width : right) - scrollAdjustment
}

const snap = (val, snapVal, rounding = Math.round) => {
  return rounding(val / snapVal) * snapVal
}

const snapSec = (x, width, snapMins, columnHeaders) => {
  // calculate time based on width and percentage
  const mosX = x + (width - 26)
  // get column
  const col = Math.floor(mosX / width)
  // percentage of current column
  const colPerc = (mosX % width) / width
  // get time based on percentage and column
  const selCol = columnHeaders.primary[col]
  const sec = selCol.sec + colPerc * (selCol.endSec - selCol.sec)

  // snap adjustments
  return snap(sec, snapMins)
}
